import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Pagination } from "../components/Pagination";
import { Modal } from "../components/Modal";
import {
  Alert,
  Button,
  buttonStyles,
  Card,
  Dropdown,
  EmptyState,
  Input,
  Mono,
  PageHeader,
  Select,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Th,
  cx,
} from "../components/ui";
import { ApiError } from "../lib/api";
import {
  officialRows,
  isFeaturedRank,
  isNewModel,
  modelEditPath,
  percent,
  toRate,
} from "../lib/aiModelForm";
import {
  useAiModels,
  type AiModelParams,
  useDeleteAiModel,
  useInferenceMargin,
  useSyncAiModels,
  useUpdateAiModel,
} from "../lib/queries";
import type {
  AiModel,
  AiModelInput,
  AiModelSyncResult,
  BillingUnit,
  ModelCategory,
} from "../lib/types";

/**
 * The AI model catalogue.
 *
 * The page is built around one distinction, and everything else follows from
 * it:
 *
 *   official  what the provider charges. A fact — synced, or dated by hand.
 *   cost      official + 5.5%, the fee the supplier takes on a credit
 *             purchase. Real money, already spent.
 *   price     what the customer pays. Derived from cost and the markup, never
 *             stored, so a synced rate change cannot leave it stale.
 *
 * An operator does not type a price. They type a markup, and the price falls
 * out — which keeps the margin the thing being chosen rather than a number that
 * happens to result. The margin is shown live as they type for the same reason.
 */

/**
 * Every unit the catalogue can hold, in the order they matter to an operator.
 *
 * `per-token` leads because it is the common one and the only one a rate card
 * describes. It is no longer the only sellable one — a generation model billed
 * from what its request cost is sold without ever being metered per token, and
 * `billingMode` rather than this column is what says so. `unpriced` is next
 * because it is the only *actionable* one: those models become sellable the
 * moment somebody reads a rate card and types the figure in.
 */
const BILLING_UNITS: BillingUnit[] = [
  "per-token",
  "unpriced",
  "per-step",
  "per-tile",
  "per-audio-minute",
  "per-character",
  "per-request",
  "other",
];

/**
 * The catalogue's own words for each category.
 *
 * Written out rather than shown raw, because `embedding` reads as a verb where
 * every other value here names a medium. The set is the database's — a check
 * constraint holds it to these five, and the three shelves the platform does not
 * sell were removed from both at once rather than left as empty tabs.
 */
const CATEGORY_LABELS: Record<ModelCategory, string> = {
  text: "Text",
  image: "Image",
  embedding: "Embeddings",
  video: "Video",
  other: "Other",
};

/**
 * A category tab, and the primary way into a 426-row catalogue.
 *
 * Reads as a tab rather than a chip — a heavier weight, an underline on the
 * selected one — because it is a change of *what you are looking at* rather than
 * a filter over it. The status chips below stayed chips for the same reason in
 * reverse.
 *
 * A zero count is dimmed rather than hidden. The tab still says the category
 * exists and the supplier has nothing in it, which is a fact worth showing once
 * an operator has wondered whether we carry video models at all.
 */
function CategoryTab({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-200 ease-smooth",
        selected
          ? "bg-fg/8 font-medium text-fg"
          : count === 0
            ? "text-fg-subtle hover:bg-fg/5"
            : "text-fg-muted hover:bg-fg/5 hover:text-fg",
      )}
    >
      {label}
      <span
        className={cx(
          "font-mono text-[0.6875rem] tabular-nums",
          count === 0 ? "text-fg-subtle/60" : "text-fg-subtle",
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * A counted filter chip.
 *
 * The count is what makes it worth being a chip rather than another select: an
 * operator scanning this row learns the catalogue's shape — 61 models, 33
 * sellable, 2 listed — before choosing anything.
 */
function StatusChip({
  label,
  count,
  selected,
  onClick,
}: {
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        "flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors duration-200 ease-smooth",
        selected
          ? "border-accent bg-accent-soft text-fg"
          : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
      )}
    >
      {label}
      <span className="font-mono text-[0.6875rem] tabular-nums text-fg-subtle">{count}</span>
    </button>
  );
}

/**
 * One row style for every item in the row menu.
 *
 * Written once rather than per button, for the reason the sidebar menus in this
 * console share theirs: six near-identical class strings is six chances for one to
 * drift, and the drift shows as one menu item sitting a pixel off from its
 * neighbours.
 */
const ROW_MENU_ITEM =
  "flex w-full items-center rounded-sm px-2.5 py-1.5 text-left text-[13px] text-fg-muted transition-colors duration-200 ease-smooth hover:bg-fg/8 hover:text-fg";

/**
 * A stack of label→figure rows, for a cell holding more than one number.
 *
 * The label sits to the *left* of the figure so the figures share a column,
 * whatever the labels are. A rate card's `in`/`out` and a generation model's
 * `image`/`MP` then occupy the same rhythm, and an operator scanning the Official
 * column down the page is comparing numbers rather than re-reading a layout on
 * every other row.
 *
 * The unit goes underneath, once, when every figure in the cell shares one —
 * which is exactly when repeating it on each row would be noise.
 */
function RateLines({
  lines,
}: {
  lines: { rows: { label: string; value: string }[]; caption: string | null; andMore: number };
}) {
  return (
    <div className="inline-block text-right">
      {lines.rows.map((row) => (
        <div key={`${row.label}-${row.value}`} className="flex items-baseline justify-end gap-2">
          <span className="text-[0.6875rem] text-fg-subtle">{row.label}</span>
          <span className="tabular-nums">{row.value}</span>
        </div>
      ))}
      {lines.andMore > 0 && (
        <div className="text-[0.6875rem] text-fg-subtle">+{lines.andMore} more</div>
      )}
      {lines.caption && (
        <div className="text-[0.6875rem] text-fg-subtle">per {lines.caption}</div>
      )}
    </div>
  );
}

/**
 * Whether the provider publishes this model at zero.
 *
 * Both sides, not either: an embedding model is priced on input alone and emits
 * no tokens to charge for, so one zero would mislabel the whole shelf. Read off
 * `official` rather than `price`, because the derived price of a zero rate is
 * also zero and the question is what the provider charges.
 */
export function isFreeModel(model: {
  official: { inputMicrosPerMTok: number; outputMicrosPerMTok: number };
  billingMode?: "token" | "upstream_cost";
}): boolean {
  /*
   * A cost-priced model is never free, however its rate card reads.
   *
   * The images and videos catalogues publish no per-token rates at all — those
   * live behind a per-model call, keyed by resolution — so `official` is
   * genuinely 0/0 for all 65 of them. Read as a rate that is "free", which is a
   * lie about a model that bills real money, and the worst possible one to show
   * an operator deciding whether to list it.
   */
  if (model.billingMode === "upstream_cost") return false;

  return model.official.inputMicrosPerMTok === 0 && model.official.outputMicrosPerMTok === 0;
}

export function getFeaturedRows(rows: AiModel[]): AiModel[] {
  return rows
    .filter((model) => isFeaturedRank(model.featuredRank))
    .sort((a, b) => a.featuredRank! - b.featuredRank! || a.id.localeCompare(b.id));
}

export function adjacentFeaturedRank(
  rows: AiModel[],
  modelId: string,
  direction: "up" | "down",
): number | null {
  const index = rows.findIndex((model) => model.id === modelId && isFeaturedRank(model.featuredRank));
  if (index < 0) return null;

  const adjacent = rows[index + (direction === "up" ? -1 : 1)];
  return adjacent && isFeaturedRank(adjacent.featuredRank) ? adjacent.featuredRank : null;
}

export function unlistInput(): AiModelInput {
  return { available: false };
}

/**
 * Which of a selection a markup reset would actually change.
 *
 * `ownMarkupBps` is null for a model already following the platform default, so
 * resetting one is a write that changes no price and still produces an audit
 * entry saying the markup changed. That is worse than doing nothing: it is a
 * false record of a pricing decision, on the surface where "who changed this
 * price, and when" is the question asked after a surprise on an invoice.
 *
 * Separated from the component so the count in the button label and the rows in
 * the confirmation come from one function rather than two filters that could
 * disagree about which models are about to move.
 */
export function markupResetTargets(rows: AiModel[]): AiModel[] {
  return rows.filter((model) => model.ownMarkupBps !== null);
}

/**
 * Tallies values, preserving first-seen order.
 *
 * Used for the sync summary's unit breakdown, where the order matters: the units
 * arrive in the order the scan met them, which puts the most common ones first
 * without a sort that would reorder equal counts arbitrarily between runs.
 */
export function countBy<T extends string>(values: T[]): Map<T, number> {
  const counts = new Map<T, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return counts;
}

const PAGE_SIZE = 25;

export function AiModelsPage() {
  /*
   * The filter set is one piece of state, and changing any part of it returns to
   * the first page. Keeping `offset` separate and resetting it on every other
   * change is the bug this shape avoids: filter down to three rows while on page
   * four and the table is empty for a reason nothing on screen explains.
   */
  const [params, setParams] = useState<AiModelParams>({
    limit: PAGE_SIZE,
    offset: 0,
    variants: "hide",
  });
  const [search, setSearch] = useState("");

  const setFilters = (next: Partial<AiModelParams>) =>
    setParams((current) => ({ ...current, ...next, offset: 0 }));
  const setOffset = (offset: number) => setParams((current) => ({ ...current, offset }));
  const resetFilters = () => {
    setSearch("");
    // `variants` survives a reset. It is a view preference rather than a filter
    // an operator is hunting with, and having it snap back to showing all 79 on
    // every clear is exactly the kind of small betrayal that makes a table tiring.
    setParams({ limit: PAGE_SIZE, offset: 0, sort: params.sort, variants: params.variants });
  };

  const { q, provider, billingUnit, status, variants, category, sort = "sort_order" } = params;
  const filtersAreEmpty =
    !q && !provider && !billingUnit && status === undefined && category === undefined;

  const models = useAiModels(params);
  const counts = models.data?.counts;

  /*
   * Realised margin per model, from the same endpoint the AI usage page reads.
   *
   * Fetched rather than joined into the catalogue query on purpose. That query
   * is paged, filtered and sorted, and hanging a per-model aggregate over
   * `inference_usage` off it would make the cost of listing the catalogue scale
   * with how much traffic the platform has served — for a column that is blank
   * on most rows. Two independent requests, joined by id in the browser, keep
   * the catalogue's cost flat.
   *
   * Thirty days because that is the shortest window in which a lightly-used
   * model has any measured requests at all. A model with none is absent from the
   * map and its row simply shows nothing.
   */
  const margin = useInferenceMargin(30);
  const measuredMargin = useMemo(
    () =>
      new Map(
        (margin.data?.byModel ?? [])
          .filter((row) => row.marginBps !== null)
          .map((row) => [row.model, row.marginBps as number]),
      ),
    [margin.data],
  );
  /*
   * Every publisher in the catalogue, not a list kept in this file.
   *
   * The constant that stood here held eight names against a catalogue of
   * fifty-nine publishers, so two thirds of it could not be filtered to at all —
   * a model from Qwen or DeepSeek was reachable only by knowing its name and
   * typing it into the search box. Read from the response, so it cannot go stale
   * again the next time the supplier adds one.
   */
  const publishers = models.data?.publishers ?? [];
  const categories = models.data?.categories ?? [];
  const updateModel = useUpdateAiModel();
  const deleteModel = useDeleteAiModel();
  const sync = useSyncAiModels();

  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AiModel | null>(null);
  const [syncResult, setSyncResult] = useState<AiModelSyncResult | null>(null);

  /**
   * Which rows are selected, by id.
   *
   * Ids rather than indices, so a refetch that reorders or repages the table cannot
   * silently move a selection onto different models — the failure mode there is
   * unlisting something nobody chose.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [confirmVerify, setConfirmVerify] = useState(false);
  const [confirmResetMarkup, setConfirmResetMarkup] = useState(false);

  async function runSync() {
    setError(null);
    setSyncResult(null);
    try {
      setSyncResult(await sync.mutateAsync());
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not sync the catalogue.");
    }
  }

  async function remove() {
    if (!confirmDelete) return;
    setError(null);
    try {
      await deleteModel.mutateAsync(confirmDelete.id);
      setConfirmDelete(null);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not delete the model.");
      setConfirmDelete(null);
    }
  }

  function updateRow(model: AiModel, input: AiModelInput) {
    setError(null);
    void updateModel.mutateAsync({ id: model.id, input }).catch((cause: unknown) =>
      setError(
        cause instanceof ApiError
          ? cause.message
          : input.available === undefined
            ? "Could not change the featured position."
            : "Could not change whether the model is listed.",
      ),
    );
  }

  /**
   * Lists or unlists everything selected.
   *
   * One request per model rather than a bulk endpoint, deliberately: each edit is
   * audited against the model it changed, and a single bulk call would collapse
   * twenty audit entries into one that names none of them. The catalogue is the
   * surface where "who listed this, and when" is the question asked after a
   * surprise on an invoice.
   *
   * Sequential rather than parallel, for the same reason the featured ranking is
   * serialized by an advisory lock — a listing can renumber neighbours, and twenty
   * concurrent renumberings is a lock contention problem with no upside at this
   * size.
   *
   * Failures are counted rather than thrown. Listing sixteen of twenty and being
   * told which four refused is useful; losing the sixteen because the fourth failed
   * is not.
   */
  /**
   * Dates the selected models' official rates as read today.
   *
   * Deliberately labelled as dating rather than verifying, and gated behind a
   * confirmation that says what the claim is. `official_verified_at` is not a
   * checkbox — it is an assertion that somebody read the provider's rate card, and
   * it is the only thing standing between the catalogue and a model sold at a margin
   * derived from a number nobody checked. A one-click "verify all" that skipped
   * saying so would turn the guard into a formality.
   *
   * The partner models are why this exists: eleven of them were seeded with
   * indicative rates and cannot be listed until a date is set, and setting eleven
   * dates through eleven modals is the kind of friction that gets solved by typing
   * today's date eleven times without reading anything.
   */
  async function bulkVerify() {
    const ids = [...selected];
    setError(null);
    setBulkRunning(true);

    // UTC, because the column is a date and the API compares it against
    // `current_date` on a server whose clock is not the operator's.
    const today = new Date().toISOString().slice(0, 10);

    const failures: { id: string; message: string }[] = [];
    for (const id of ids) {
      try {
        await updateModel.mutateAsync({ id, input: { officialVerifiedAt: today } });
      } catch (cause) {
        failures.push({
          id,
          message: cause instanceof ApiError ? cause.message : "The change was refused.",
        });
      }
    }

    setBulkRunning(false);
    setConfirmVerify(false);
    setSelected(new Set());

    if (failures.length > 0) {
      setError(
        `${ids.length - failures.length} of ${ids.length} dated. ` +
          `${failures.length} refused: ${failures
            .slice(0, 3)
            .map((failure) => `${failure.id} — ${failure.message}`)
            .join("; ")}`,
      );
    }
  }

  /**
   * Returns the selected models to the platform default markup.
   *
   * `markup_bps` is nullable, and null is not "no markup" — it is "follow the
   * default". That distinction is the whole reason this action is worth having:
   * changing the default reprices every model that follows it the moment the
   * Worker deploys, and silently skips every model carrying an override. After
   * a pricing decision an operator has no way to undo those one at a time
   * without opening each model's form.
   *
   * Only the models that actually carry an override are sent. A model already
   * on the default would be a no-op write that still produces an audit entry
   * saying its markup changed, which is worse than nothing: it is a false
   * record of a pricing change.
   */
  async function bulkResetMarkup() {
    const ids = selectedOwnMarkup.map((model) => model.id);
    setError(null);
    setBulkRunning(true);

    const failures: { id: string; message: string }[] = [];
    for (const id of ids) {
      try {
        await updateModel.mutateAsync({ id, input: { markupBps: null } });
      } catch (cause) {
        failures.push({
          id,
          message: cause instanceof ApiError ? cause.message : "The change was refused.",
        });
      }
    }

    setBulkRunning(false);
    setConfirmResetMarkup(false);
    setSelected(new Set());

    if (failures.length > 0) {
      setError(
        `${ids.length - failures.length} of ${ids.length} reset. ` +
          `${failures.length} refused: ${failures
            .slice(0, 3)
            .map((failure) => `${failure.id} — ${failure.message}`)
            .join("; ")}${failures.length > 3 ? `; and ${failures.length - 3} more` : ""}`,
      );
    }
  }

  async function bulkList(available: boolean) {
    const ids = [...selected];
    setError(null);
    setBulkRunning(true);

    const failures: { id: string; message: string }[] = [];
    for (const id of ids) {
      try {
        await updateModel.mutateAsync({ id, input: { available } });
      } catch (cause) {
        failures.push({
          id,
          message: cause instanceof ApiError ? cause.message : "The change was refused.",
        });
      }
    }

    setBulkRunning(false);
    setSelected(new Set());

    if (failures.length > 0) {
      setError(
        `${ids.length - failures.length} of ${ids.length} changed. ` +
          `${failures.length} refused: ${failures
            .slice(0, 3)
            .map((failure) => `${failure.id} — ${failure.message}`)
            .join("; ")}${failures.length > 3 ? `; and ${failures.length - 3} more` : ""}`,
      );
    }
  }

  const rows = models.data?.data ?? [];

  /*
   * Counted over the current page's rows, which is the only place the selection can
   * have come from. `List 12` on a selection of sixteen says what will happen before
   * it happens — the other four are unsellable and the API would refuse them, so
   * promising twenty and delivering sixteen would be the wrong way round.
   */
  const selectedRows = rows.filter((model) => selected.has(model.id));
  const selectedSellable = selectedRows.filter((model) => model.sellable).length;
  const selectedUnsellable = selectedRows.length - selectedSellable;
  const selectedOwnMarkup = markupResetTargets(selectedRows);
  const selectableIds = rows.map((model) => model.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggleSelected = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const featuredRows = getFeaturedRows(rows);
  const nextFeaturedRank = featuredRows.length + 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI models"
        description="What the inference API offers, and what each call is sold at."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" icon="refresh" loading={sync.isPending} onClick={() => void runSync()}>
              Sync catalogue
            </Button>
            <Link to="/ai-models/new" className={buttonStyles("primary", "md")}>
              New model
            </Link>
          </div>
        }
      />

      {error && !open && <Alert>{error}</Alert>}

      {syncResult && <SyncSummary result={syncResult} onDismiss={() => setSyncResult(null)} />}

      {/*
        The catalogue's shape, as the filters an operator actually reaches for.
        Counts are over the whole table rather than the page — a chip reading
        "unsellable 3" beside a page that happens to hold three of twenty-eight
        would mislead in exactly the situation it exists for.
      */}
      {/*
        Categories first, status second, and that order is the point.
        "What kind of model" is how an operator narrows 426 rows to something
        readable; "listed or not" is what they ask once they are already looking
        at twenty. Reversing them made the catalogue feel like one undifferentiated
        list with a filter bolted on.

        Empty categories are shown rather than hidden. "Video 0" says the supplier
        carries none; a missing tab says nothing and reads as an oversight.

        These replaced a Task select — Chat, Embeddings, Not servable — and it is
        gone rather than kept beside them. Both are read from the supplier's own
        `output_modalities`, but `modality` flattens that to three values because
        it answers a narrower question: which of our two endpoints can serve the
        row. As a filter it said less than these tabs do and in the supplier's
        vocabulary rather than its own — Chat was Text and Transcription together,
        Embeddings was this tab exactly, and Not servable was the Unsellable chip
        below with a reason attached. The column stays; the inference endpoints
        gate on it, and nothing here needed to ask by it.
      */}
      {categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 border-b border-line pb-3">
          <CategoryTab
            label="All models"
            count={counts?.total ?? 0}
            selected={category === undefined}
            onClick={() => setFilters({ category: undefined })}
          />
          {categories.map((entry) => (
            <CategoryTab
              key={entry.category}
              label={CATEGORY_LABELS[entry.category]}
              count={entry.total}
              selected={category === entry.category}
              onClick={() => setFilters({ category: entry.category })}
            />
          ))}
        </div>
      )}

      {counts && (
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusChip
            label="All"
            count={counts.total}
            selected={status === undefined}
            onClick={() => setFilters({ status: undefined })}
          />
          <StatusChip
            label="Listed"
            count={counts.listed}
            selected={status === "listed"}
            onClick={() => setFilters({ status: "listed" })}
          />
          {/* Beside Listed rather than at the end, because the two are
              complements and reading them apart is the point: a sync lands new
              models here, and this is where an operator sees how many are
              waiting on a decision. */}
          <StatusChip
            label="Unlisted"
            count={counts.unlisted}
            selected={status === "unlisted"}
            onClick={() => setFilters({ status: "unlisted" })}
          />
          {/*
            After Unlisted, because it is what Unlisted used to contain. These
            are the supplier's decision rather than ours, and the only thing an
            operator can do about one is know it happened — so the chip sits out
            of the working order, and does not appear at all until there is
            something in it.
          */}
          {counts.retired > 0 && (
            <StatusChip
              label="Retired"
              count={counts.retired}
              selected={status === "retired"}
              onClick={() => setFilters({ status: "retired" })}
            />
          )}
          <StatusChip
            label="Sellable"
            count={counts.sellable}
            selected={status === "sellable"}
            onClick={() => setFilters({ status: "sellable" })}
          />
          {/* The category this whole change exists to surface: models the provider
              offers that the platform cannot meter or serve. They used to be
              dropped by the sync, so their count was unknowable from here. */}
          <StatusChip
            label="Unsellable"
            count={counts.unsellable}
            selected={status === "unsellable"}
            onClick={() => setFilters({ status: "unsellable" })}
          />
          <StatusChip
            label="Unverified rate"
            count={counts.unverified}
            selected={status === "unverified"}
            onClick={() => setFilters({ status: "unverified" })}
          />
        </div>
      )}

      <Card>
        {/*
          Search takes the full width on a phone and the selects pair up beneath it,
          rather than four controls each wrapping onto a line of their own. `basis`
          with `flex-1` is what makes them share a row at two per line and then widen
          as space allows — a fixed width would leave a ragged gap at every size
          between the breakpoints.
        */}
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-4 sm:gap-3">
          <Input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setFilters({ q: e.target.value || undefined });
            }}
            placeholder="Search id, name, upstream id or task"
            aria-label="Search the catalogue"
            className="w-full min-w-0 sm:w-auto sm:max-w-xs sm:flex-1"
          />
          <Select
            className="min-w-0 flex-1 basis-[8.5rem] sm:flex-none"
            aria-label="Billing unit"
            value={billingUnit ?? ""}
            onChange={(e) => setFilters({ billingUnit: (e.target.value || undefined) as never })}
          >
            <option value="">Any unit</option>
            {BILLING_UNITS.map((unit) => (
              <option key={unit} value={unit}>
                {unit.replace(/-/g, " ")}
              </option>
            ))}
          </Select>
          <Select
            className="min-w-0 flex-1 basis-[8.5rem] sm:flex-none"
            aria-label="Publisher"
            value={provider ?? ""}
            onChange={(e) => setFilters({ provider: e.target.value || undefined })}
          >
            <option value="">Any publisher</option>
            {publishers.map((entry) => (
              <option key={entry.provider} value={entry.provider}>
                {entry.provider} ({entry.total})
              </option>
            ))}
          </Select>
          <Select
            className="min-w-0 flex-1 basis-[8.5rem] sm:flex-none"
            aria-label="Variants"
            value={variants ?? ""}
            onChange={(e) => setFilters({ variants: (e.target.value || undefined) as never })}
          >
            {/*
              A model and its :batch or :free twin are the same model at a different
              price. Hiding them is the default because browsing with 79 of them
              interleaved is most of what made this table feel unmanageable.
            */}
            <option value="hide">Hide variants</option>
            <option value="">Show variants</option>
            <option value="only">Variants only</option>
          </Select>
          <Select
            className="min-w-0 flex-1 basis-[8.5rem] sm:flex-none"
            aria-label="Sort"
            value={sort}
            onChange={(e) => setFilters({ sort: e.target.value as never })}
          >
            <option value="sort_order">Display order</option>
            <option value="newest">Newest</option>
            <option value="name">Name</option>
            <option value="cheapest">Cheapest official</option>
            <option value="dearest">Dearest official</option>
            <option value="margin">Thinnest margin</option>
          </Select>
          {!filtersAreEmpty && (
            <Button variant="ghost" size="sm" onClick={() => resetFilters()}>
              Clear
            </Button>
          )}
        </div>

        {/*
          The bulk bar replaces the toolbar rather than sitting beside it, because
          while a selection exists it is the only thing an operator is about to act
          on — and a filter change would clear the selection out from under them.
        */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-raised px-4 py-2.5">
            <p className="text-sm text-fg">
              <span className="font-mono tabular-nums">{selected.size}</span> selected
              {selectedUnsellable > 0 && (
                <span className="ml-2 text-xs text-warn">
                  {selectedUnsellable} cannot be listed
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                loading={bulkRunning}
                disabled={selectedSellable === 0}
                onClick={() => void bulkList(true)}
              >
                List {selectedSellable > 0 ? selectedSellable : ""}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                loading={bulkRunning}
                onClick={() => void bulkList(false)}
              >
                Unlist
              </Button>
              {/*
                Named for what it does rather than what it achieves. It writes today's
                date into `official_verified_at`, which is an assertion that somebody
                read the provider's rate card — so the label says "date" and the
                confirmation says what is being claimed. "Verify" on a button that
                checks nothing is how the guard becomes a formality.
              */}
              <Button
                variant="secondary"
                size="sm"
                loading={bulkRunning}
                onClick={() => setConfirmVerify(true)}
              >
                Date rates today
              </Button>
              {/*
                Counted in the label, like List, and disabled when the count is
                zero. A selection of twenty where three carry an override should
                say three before it is pressed rather than report it afterwards.
              */}
              <Button
                variant="secondary"
                size="sm"
                loading={bulkRunning}
                disabled={selectedOwnMarkup.length === 0}
                onClick={() => setConfirmResetMarkup(true)}
              >
                Reset markup {selectedOwnMarkup.length > 0 ? selectedOwnMarkup.length : ""}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {models.isLoading ? (
          <TableSkeleton columns={7} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon="models"
            title={filtersAreEmpty ? "No models yet" : "No model matches these filters"}
            description={
              filtersAreEmpty
                ? "Sync catalogue to pull OpenRouter's catalogue, or add a partner model by hand."
                : "Clear a filter, or search for a different id or task."
            }
            action={
              filtersAreEmpty ? (
                <Button onClick={() => void runSync()}>Sync catalogue</Button>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => resetFilters()}>
                  Clear filters
                </Button>
              )
            }
          />
        ) : (
          <>
          <Table
            head={
              <tr>
                <Th className="w-8">
                  {/* Selects this page, not the catalogue. A control that silently
                      selected sixty-one rows from a view showing twenty-five is how
                      somebody unlists something they never saw. */}
                  <input
                    type="checkbox"
                    className="size-3.5 accent-accent"
                    aria-label="Select every model on this page"
                    checked={allSelected}
                    onChange={() =>
                      setSelected(allSelected ? new Set() : new Set(selectableIds))
                    }
                  />
                </Th>
                {/*
                  Seven columns, from eleven, ordered the way the questions arrive:
                  which model, is it live, what does a customer pay, what do we keep,
                  and what is that derived from.
                    * State moved up from ninth. It is the first thing anyone scans
                      for and it was sitting past the fold on a narrow window.
                    * Sell leads the numbers because it is the published figure;
                      official follows as the fact behind it, muted.
                    * Cost is gone. It is the intermediate step, and margin already
                      says what it was there to say — below cost is coloured red. The
                      full official → cost → sell derivation is still spelled out in
                      the edit form, live, which is where a price is actually chosen.
                    * Provider, billing unit, task and featured rank all fold into
                      the model cell. Four columns that were empty or identical on
                      almost every row, and only interesting on the rows where they
                      are not.
                */}
                <Th>Model</Th>
                {/* `always`, not `sm`. The rule in ui.tsx is that nothing which is
                    the only copy of a fact may be hidden, and after this reorder
                    state is exactly that — the provider, unit and featured rank it
                    used to sit beside are inline in the model cell now, so this tag
                    is the only place "listed" appears. It is also what a phone-sized
                    window is most likely to be checking. */}
                <Th>State</Th>
                <Th numeric>Sell /M</Th>
                <Th numeric priority="sm">Margin</Th>
                <Th numeric priority="md">Official /M</Th>
                <Th />
              </tr>
            }
          >
            {rows.map((model) => (
              <ModelRow
                key={model.id}
                model={model}
                defaultMarkupBps={models.data?.defaultMarkupBps ?? 0}
                measuredBps={measuredMargin.get(model.id) ?? null}
                selected={selected.has(model.id)}
                onSelect={() => toggleSelected(model.id)}
                featured={featuredRows}
                nextFeaturedRank={nextFeaturedRank}
                onUpdate={(input) => updateRow(model, input)}
                onDelete={() => {
                  setError(null);
                  setConfirmDelete(model);
                }}
              />
            ))}
          </Table>

          {models.data && (
            <Pagination
              total={models.data.total}
              limit={models.data.limit}
              offset={models.data.offset}
              onChange={setOffset}
            />
          )}

          <p className="border-t border-line px-4 py-2.5 text-xs text-fg-subtle">
            Sell prices come from the official rate plus the markup, so a sync that moves a
            provider&rsquo;s rate changes what customers pay.
            {models.data?.lastSyncedAt
              ? ` Last synced ${new Date(models.data.lastSyncedAt).toLocaleString()}.`
              : " Never synced."}
          </p>
          </>
        )}
      </Card>

      <Modal
        open={confirmResetMarkup}
        onClose={() => setConfirmResetMarkup(false)}
        title={`Return ${selectedOwnMarkup.length} model${
          selectedOwnMarkup.length === 1 ? "" : "s"
        } to the platform markup?`}
        description="This changes what customers pay, on the next request."
      >
        <div className="space-y-4">
          <Alert tone="warning">
            Clearing a model's own markup makes it follow the platform default of{" "}
            {percent(models.data?.defaultMarkupBps ?? 0)}. Prices are worked out on each request,
            so this takes effect immediately. There is no separate save.
          </Alert>
          <p className="text-sm leading-6 text-fg-muted">
            Blank is not a markup of zero. It means "whatever the platform charges", so these
            models move again the next time that number changes. That is the point of resetting
            them, and the reason to check none was priced deliberately.
          </p>
          {/*
            Each model's current markup beside what it becomes. The list is the
            whole confirmation: "3 models" says nothing about whether one of them
            was set to 40% for a reason somebody still remembers.
          */}
          <div className="max-h-40 overflow-y-auto rounded-md border border-line bg-canvas p-3">
            <ul className="space-y-0.5">
              {selectedOwnMarkup.map((model) => (
                <li key={model.id} className="text-xs text-fg-subtle">
                  <Mono className="text-xs">{model.id}</Mono>
                  <span className="ml-2 tabular-nums">
                    {percent(model.ownMarkupBps ?? 0)} →{" "}
                    {percent(models.data?.defaultMarkupBps ?? 0)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmResetMarkup(false)}>
              Cancel
            </Button>
            <Button loading={bulkRunning} onClick={() => void bulkResetMarkup()}>
              Reset to platform markup
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmVerify}
        onClose={() => setConfirmVerify(false)}
        title={`Record ${selected.size} rate${selected.size === 1 ? "" : "s"} as verified today?`}
        description="This is a claim, not a check. Nothing here reads a rate card."
      >
        <div className="space-y-4">
          <Alert tone="warning">
            You are recording that today you checked each of these models' input and output rates
            against the provider's rate card, and they matched.
          </Alert>
          <p className="text-sm leading-6 text-fg-muted">
            This date is what lets a model be listed, and the sell price comes from the rate it
            dates. Recording it without reading the rate card sells the model at a margin nobody
            checked — which shows up a month later, on an invoice, for every call in between.
          </p>
          {/*
            The models are named. Twenty ids is a wall, but the point of the list is that
            somebody about to make this claim sees exactly what they are claiming it about.
          */}
          <div className="max-h-40 overflow-y-auto rounded-md border border-line bg-canvas p-3">
            <ul className="space-y-0.5">
              {selectedRows.map((model) => (
                <li key={model.id} className="text-xs text-fg-subtle">
                  <Mono className="text-xs">{model.id}</Mono>
                  {model.officialVerifiedAt !== null && (
                    <span className="ml-2">already dated {model.officialVerifiedAt}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmVerify(false)}>
              Cancel
            </Button>
            <Button loading={bulkRunning} onClick={() => void bulkVerify()}>
              Record as verified
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={`Delete ${confirmDelete?.displayName ?? ""}?`}
        description="Unlisting is usually better — it hides the model while keeping its recorded usage readable."
      >
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleteModel.isPending} onClick={() => void remove()}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function ModelRow({
  model,
  defaultMarkupBps,
  measuredBps,
  selected,
  onSelect,
  featured,
  nextFeaturedRank,
  onUpdate,
  onDelete,
}: {
  model: AiModel;
  /** The platform markup, for a model that carries no override of its own. */
  defaultMarkupBps: number;
  /**
   * Margin against what the supplier *actually* billed, over the last 30 days.
   * Null when nothing has been called, or nothing called carried a cost.
   *
   * The column beside it divides by the supplier's published rate, which is an
   * upper bound on cost rather than the cost — measured against live traffic one
   * model bills at 0.48x its list price and another at exactly 1.00x. So the
   * derived figure understates margin by an amount nobody can predict from the
   * catalogue alone, and this is the one that says what was earned.
   */
  measuredBps: number | null;
  selected: boolean;
  onSelect: () => void;
  featured: AiModel[];
  nextFeaturedRank: number;
  onUpdate: (input: AiModelInput) => void;
  onDelete: () => void;
}) {
  const rank = isFeaturedRank(model.featuredRank) ? model.featuredRank : null;
  const upRank = adjacentFeaturedRank(featured, model.id, "up");
  const downRank = adjacentFeaturedRank(featured, model.id, "down");

  return (
    <tr className={cx(!model.available && "opacity-60", selected && "bg-accent-soft/40")}>
      <Td>
        <input
          type="checkbox"
          className="size-3.5 accent-accent"
          aria-label={`Select ${model.displayName}`}
          checked={selected}
          onChange={onSelect}
        />
      </Td>
      {/*
        Identity, and everything that is only worth a glance.

        Provider, billing unit, task and featured rank were four columns of their
        own. Each was identical or empty on nearly every row — `workers-ai`,
        `per-token`, `Text Generation`, `—` — so they cost a column of width to say
        nothing, and buried the rows where they said something. Inline and
        conditional, they now appear exactly when they differ from the boring case.
      */}
      <Td>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-medium text-fg">{model.displayName}</span>
          {/* The same seven-day window the marketplace marks, so an operator
              answering "is this new?" reads the customer's answer rather than
              deriving one from a timestamp column. */}
          {isNewModel(model) && (
            <span className="border border-accent/40 bg-accent-soft px-1.5 py-0.5 text-[10px] tracking-[0.08em] text-accent uppercase">
              New
            </span>
          )}
          {rank !== null && (
            <span className="font-mono text-[0.6875rem] text-accent" title="Featured position">
              ★{rank}
            </span>
          )}
          {/* Only when it is not the default. `workers-ai` on sixty rows is noise;
              `openai` on one is the thing worth seeing. */}
          {model.provider !== "workers-ai" && (
            <span className="text-[0.6875rem] text-fg-subtle">{model.provider}</span>
          )}
        </div>

        {/* The public id first, because it is the one a customer will quote in a
            support question. The routing id after it, and only when the two
            differ — repeating an identical string reads as a bug. */}
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
          <Mono className="text-xs text-fg-subtle">{model.id}</Mono>
          {model.upstreamId !== model.id && (
            <span title={model.upstreamId}>
              <Mono className="text-[0.6875rem] text-fg-subtle">→ {model.upstreamId}</Mono>
            </span>
          )}
        </div>

        {/* The unit only when it is not per-token, which is the only time it
            explains anything — and when it does, it is why the row cannot be sold. */}
        {!model.sellable && (
          <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-[0.6875rem] text-fg-subtle">
            <span className="text-warn">{model.billingUnit.replace(/-/g, " ")}</span>
            {model.task && <span>{model.task}</span>}
          </div>
        )}
      </Td>

      {/*
        Moved up from ninth. It is what anyone scans this table for first, and on a
        narrow window it was sitting past the fold behind four numeric columns.
      */}
      <Td>
        {!model.sellable ? (
          <span title={model.unsellableReason ?? undefined}>
            <Tag tone="warn">not sellable</Tag>
          </span>
        ) : model.available ? (
          <Tag tone="good">listed</Tag>
        ) : model.retiredAt ? (
          /*
            Ahead of both branches below, because it is the fact that explains
            them. A retired model is unlisted and often unverified as well, and
            either of those labels invites an operator to act on a row whose only
            correct action is none.
          */
          <Tag tone="neutral">retired</Tag>
        ) : model.officialVerifiedAt === null ? (
          <Tag tone="warn">unverified</Tag>
        ) : (
          <Tag tone="neutral">unlisted</Tag>
        )}
      </Td>

      {/* The published figure leads. It is what a customer pays. */}
      <Td numeric>
        {model.billingMode === "upstream_cost" ? (
          /* No rate card to show. Said in words rather than as a number, because
             every number available here is zero and all of them read as free. */
          <span title="Billed at what the provider charged, plus this model's markup">
            <Tag tone="neutral">at cost + markup</Tag>
          </span>
        ) : isFreeModel(model) ? (
          <Tag tone="neutral">free</Tag>
        ) : (
          <>
            {model.price.input} / {model.price.output}
          </>
        )}
      </Td>

      {/*
        The one number worth colouring. Below cost is a loss on every call, and it
        looks exactly like a healthy price otherwise.

        A free model has no margin to have, and 0.0% in amber says the opposite —
        it reads as a thin margin somebody should widen. An operator who learns to
        ignore that warning has lost the warning on the rows that mean it.
      */}
      <Td numeric priority="sm">
        {model.billingMode === "upstream_cost" ? (
          /*
           * The markup itself, because for these it *is* the margin.
           *
           * `marginBps` is derived from the official rate, and a model whose rate
           * card is empty derives 0.0% — which the colouring below then reads as a
           * margin somebody should widen. It is not thin, it is not computable
           * from a rate that does not exist: sell is cost x (1 + markup), so the
           * markup is the margin exactly.
           */
          <span title="This model has no rate card; the markup is the margin">
            {percent(model.ownMarkupBps ?? defaultMarkupBps)}
          </span>
        ) : isFreeModel(model) ? (
          <span className="text-fg-subtle">—</span>
        ) : (
          <span
            className={cx(
              model.marginBps.input < 0 && "text-danger",
              model.marginBps.input >= 0 && model.marginBps.input < 500 && "text-warn",
            )}
            title="Against the supplier's published rate, which is the most it can cost"
          >
            {percent(model.marginBps.input)}
          </span>
        )}

        {/*
          What the model actually earned, under the figure derived from list
          price. Both, rather than a replacement, because they answer different
          questions and each is the only answer available in some case: the
          derived one exists for a model nobody has called, and this one is the
          only one that is a fact.

          Muted and smaller because the derived figure is the one being *set* on
          this page — an operator moves a markup and watches that number. This is
          the reality check beside it, and its absence is not a warning: most
          models have no traffic.
        */}
        {measuredBps !== null && (
          <div
            className="mt-0.5 text-[10px] text-fg-subtle tabular-nums"
            title="Measured: charged against what the supplier billed, last 30 days"
          >
            {percent(measuredBps)} real
          </div>
        )}

        {/*
          The stale-basis warning, and the reason this column can be trusted at
          all. A cost basis is a fraction of the supplier's published rate; when
          that rate moves, the fraction is measured against a baseline that no
          longer exists and the model sells at a price nobody chose.

          Amber rather than danger because the direction is unknown — the price
          may be too low or too high, and only re-reading the measured margin
          settles which. Danger would claim we know it is a loss.

          Never shown on a model at 1.00x: that is the absence of a claim about
          cost, and flagging 487 untouched rows on the first supplier reprice
          would teach an operator to ignore the warning entirely.
        */}
        {model.costFactorStale && (
          <div
            className="mt-0.5 text-[10px] text-warn"
            title="The provider's rate changed after this cost basis was set. Re-check it against the measured margin."
          >
            basis stale
          </div>
        )}
      </Td>

      {/* The fact the two above are derived from, muted and last of the numbers. */}
      <Td numeric priority="md" className="text-fg-subtle">
        {model.billingMode === "upstream_cost" ? (
          /*
           * The supplier's own per-unit prices, which are the fact behind the two
           * columns above for these models exactly as the rate card is for the
           * rest.
           *
           * This was a dash, with a comment saying the figure "is not known until
           * the call is made". That was wrong: OpenRouter publishes it, on a
           * sub-resource for images and under a differently-named key for videos,
           * and the sync had never read either. An operator setting a markup was
           * doing it against a column that said there was nothing to know.
           *
           * Still a dash when the list is empty, because three image models
           * genuinely publish no price — and that is now a statement about the
           * supplier rather than about our reading of it.
           */
          (model.priceLines ?? []).length > 0 ? (
            /*
             * Label and figure split, so the numbers stack in one column.
             *
             * This cell printed `$0.035 / megapixel` with the unit inside the
             * value, which is unreadable at two lines in a dense table: nothing
             * aligns, and the reader parses a sentence where they wanted to
             * compare a figure. The label carries the unit now, the same way it
             * does on both customer surfaces — an operator setting a markup
             * should be reading the strings a customer reads.
             */
            <span title="What the provider charges per unit, before our markup">
              <RateLines lines={officialRows(model)} />
            </span>
          ) : (
            <span title="The provider publishes no price for this model at all">—</span>
          )
        ) : (
          <RateLines lines={officialRows(model)} />
        )}
      </Td>
      {/*
        One verb inline, the rest behind a menu.

        This cell carried up to six ghost buttons — Edit, Unfeature, Up, Down,
        Unlist, Delete — which made it the widest column in the table and forced a
        horizontal scroll on any narrow window. Listing is the decision this page
        exists for, so it stays a button; everything else is a menu, which is also
        the difference between a stray tap on a phone costing a reorder and costing
        nothing.
      */}
      <Td className="text-right">
        <div className="flex items-center justify-end gap-1">
          {/*
            No List button on a model that cannot be listed. The API refuses it and
            a check constraint refuses it under that, so offering the button would
            only ever produce an error — and an operator who clicks it learns the
            rule by being told off. The reason is on the disabled control instead,
            which is where somebody hovers when they wonder why.
          */}
          {model.available ? (
            <Button variant="ghost" size="sm" onClick={() => onUpdate(unlistInput())}>
              Unlist
            </Button>
          ) : model.sellable ? (
            <Button variant="ghost" size="sm" onClick={() => onUpdate({ available: true })}>
              List
            </Button>
          ) : (
            <span title={model.unsellableReason ?? undefined}>
              <Button variant="ghost" size="sm" disabled>
                List
              </Button>
            </span>
          )}

          <Dropdown
            align="end"
            trigger={<span aria-label={`More actions for ${model.displayName}`}>⋯</span>}
            triggerClassName="rounded-md px-2 py-1 text-sm text-fg-subtle transition-colors duration-200 ease-smooth hover:bg-raised hover:text-fg"
          >
            {/* A link, not a button that opens a dialog. The editor is a route
                now, so this can be opened in a new tab, copied into a channel,
                and reached again by the back button. */}
            <Link to={modelEditPath(model.id)} className={ROW_MENU_ITEM}>
              Edit
            </Link>

            {model.available && rank === null && (
              <button
                type="button"
                className={ROW_MENU_ITEM}
                onClick={() => onUpdate({ featuredRank: nextFeaturedRank })}
              >
                Feature
              </button>
            )}

            {model.available && rank !== null && (
              <>
                <button
                  type="button"
                  className={ROW_MENU_ITEM}
                  onClick={() => onUpdate({ featuredRank: null })}
                >
                  Unfeature
                </button>
                {upRank !== null && (
                  <button
                    type="button"
                    className={ROW_MENU_ITEM}
                    onClick={() => onUpdate({ featuredRank: upRank })}
                  >
                    Move up
                  </button>
                )}
                {downRank !== null && (
                  <button
                    type="button"
                    className={ROW_MENU_ITEM}
                    onClick={() => onUpdate({ featuredRank: downRank })}
                  >
                    Move down
                  </button>
                )}
              </>
            )}

            <button
              type="button"
              className={cx(ROW_MENU_ITEM, "text-danger hover:bg-danger/8 hover:text-danger")}
              onClick={onDelete}
            >
              Delete
            </button>
          </Dropdown>
        </div>
      </Td>
    </tr>
  );
}

/**
 * What a sync did.
 *
 * Ordered by consequence rather than by how the outcome reads. `unlisted` leads:
 * a model the provider has withdrawn stopped answering the moment this ran, and
 * anybody who was calling it is now getting a 404. `repriced` is next — sell
 * prices are derived, so a moved official rate has already changed what
 * customers pay. New models arrive unlisted and can wait.
 */
function SyncSummary({
  result,
  onDismiss,
}: {
  result: AiModelSyncResult;
  onDismiss: () => void;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="space-y-2 text-sm">
          <p className="text-fg">
            Scanned {result.scanned} models — {result.added.length} new, {result.repriced.length}{" "}
            repriced, {result.unchanged} unchanged, {result.unlisted.length} withdrawn.
          </p>

          {result.unlisted.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-warn">
                No longer offered upstream, so these have been unlisted and now 404:
              </p>
              <ul className="space-y-0.5">
                {result.unlisted.map((id) => (
                  <li key={id} className="text-xs text-fg-subtle">
                    <Mono>{id}</Mono>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-fg-subtle">
                The catalogue row and its usage history are kept, so re-listing it is one toggle if
                the provider brings it back.
              </p>
            </div>
          )}

          {result.unsupported.length > 0 && (
            <p className="text-xs text-fg-subtle">
              {result.unsupported.length} models skipped: floating aliases whose target model
              changes without notice.
            </p>
          )}

          {result.collisions.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-warn">
                Discovered but not added — another model already holds the public id:
              </p>
              <ul className="space-y-0.5">
                {result.collisions.map((entry) => (
                  <li key={entry.upstreamId} className="text-xs text-fg-subtle">
                    <Mono>{entry.upstreamId}</Mono> wants <Mono>{entry.alias}</Mono>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-fg-subtle">
                Rename the model that holds it, or add this one by hand under a different public id.
              </p>
            </div>
          )}

          {result.repriced.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-warn">
                These rates moved, so what customers pay has changed:
              </p>
              <ul className="space-y-0.5">
                {result.repriced.map((entry) => (
                  <li key={entry.id} className="text-xs text-fg-subtle">
                    <Mono>{entry.id}</Mono> {toRate(entry.from[0])}/{toRate(entry.from[1])} →{" "}
                    <span className="text-fg">
                      {toRate(entry.to[0])}/{toRate(entry.to[1])}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {result.added.length > 0 && (
            <p className="text-xs text-fg-subtle">
              New models arrive unlisted. Review each price before listing it.
            </p>
          )}

          {/*
            Recorded, not discarded — which is the change worth stating plainly,
            because the previous wording ("skipped") described models that were
            never written and therefore could not be looked at. Grouped by unit
            rather than listed by name: twenty-eight ids is a wall, and the useful
            reading is "eleven priced per step, five per audio minute".
          */}
          {result.unsellable.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-fg-subtle">
                {result.unsellable.length} recorded but not sellable — the token meter cannot bill
                them. They are in the catalogue and filterable under{" "}
                <span className="text-fg-muted">Unsellable</span>.
              </p>
              <ul className="flex flex-wrap gap-x-3 gap-y-0.5">
                {[...countBy(result.unsellable.map((entry) => entry.unit))].map(([unit, count]) => (
                  <li key={unit} className="text-xs text-fg-subtle">
                    <span className="font-mono tabular-nums text-fg-muted">{count}</span>{" "}
                    {unit.replace(/-/g, " ")}
                  </li>
                ))}
              </ul>
              {result.unsellable.some((entry) => entry.unit === "unpriced") && (
                <p className="text-xs text-fg-subtle">
                  The <span className="text-fg-muted">unpriced</span> ones are the actionable set:
                  the provider publishes no rate, so entering one by hand makes them sellable.
                </p>
              )}
            </div>
          )}
        </div>

        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </Card>
  );
}
